import { useCallback, useLayoutEffect, useRef, type ReactElement } from 'react';
import { type CycledEnergyMeasurementRecord } from '../../../services/battery-monitoring-api/types/battery-monitoring-api';

import drawCycledEnergyChart from './utils/draw-cycled-energy-chart';
import styles from './style.module.scss';

export interface ChartProperties {
  readonly records: CycledEnergyMeasurementRecord[];
}

const Chart = ({ records }: ChartProperties): ReactElement => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const redraw = useCallback((): void => {
    if (!svgRef.current || !viewportRef.current) return;

    drawCycledEnergyChart({
      containerElement: viewportRef.current,
      svgElement: svgRef.current,
      records,
    });
  }, [records]);

  useLayoutEffect(() => {
    redraw();

    const viewportElement = viewportRef.current;
    if (!viewportElement) return;

    const resizeObserver = new ResizeObserver(() => {
      redraw();
    });

    resizeObserver.observe(viewportElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [redraw]);

  return (
    <div className={styles.container}>
      <div className={styles.unit}>[kWh/km]</div>
      <div ref={viewportRef} className={styles.viewport} data-testid="cycled-energy-chart-viewport">
        <svg ref={svgRef} className={styles.svg} role="img" />
      </div>
    </div>
  );
};

export default Chart;
